import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import React, { useState, useEffect } from "react";
import {
    Table, Button, Card, Typography, Empty, Tag, Input,
    Modal, Drawer, Badge, Divider, Collapse, Image, Space,
} from "antd";
import { DatePicker } from "antd";
import type { TablePaginationConfig } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
    ReloadOutlined,
    PhoneOutlined,
    SearchOutlined,
    ClearOutlined,
    UserOutlined,
    ShoppingOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    StarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    LinkOutlined,
    AudioOutlined,
    AudioMutedOutlined,
} from "@ant-design/icons";
import { PipecatClient, RTVIEvent } from '@pipecat-ai/client-js';
import { WebSocketTransport, ProtobufFrameSerializer } from '@pipecat-ai/websocket-transport';
import moment from "moment";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ChartTooltip } from "recharts";
import StitchLayout from "../../components/Crm/CrmLayout";

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Quick filter presets
const QUICK_FILTERS = [
    { label: "Today", days: 0 },
    { label: "Yesterday", days: 1 },
    { label: "Last 3 days", days: 3 },
    { label: "Last 7 days", days: 7 },
    { label: "Last 30 days", days: 30 },
    { label: "Last 90 days", days: 90 },
] as const;

const SamairaCallTrack: React.FC = () => {
    const [records, setRecords] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total_records: 0, total_pages: 1 });
    const [phoneFilter, setPhoneFilter] = useState("");
    const [inputPhone, setInputPhone] = useState("");
    const [selectedRecord, setSelectedRecord] = useState<any>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [activeQuick, setActiveQuick] = useState<string | null>(null);
    const [analytics, setAnalytics] = useState<any>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<"analytics" | "calls">("analytics");

    // Call Simulator States
    const [simulatorOpen, setSimulatorOpen] = useState(false);
    const [simPhone, setSimPhone] = useState("9415332242");
    const [simProductId, setSimProductId] = useState("MSNO5WN3316R51");
    const [simStatus, setSimStatus] = useState<"idle" | "requesting" | "connecting" | "connected" | "ended" | "error">("idle");
    const [simStatusText, setSimStatusText] = useState("Ready to start call simulation");
    const [simMessages, setSimMessages] = useState<any[]>([]);
    const [isBotSpeaking, setIsBotSpeaking] = useState(false);
    const [isUserSpeaking, setIsUserSpeaking] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [activeClient, setActiveClient] = useState<any>(null);
    const [simTimer, setSimTimer] = useState(0);
    const callAbortedRef = React.useRef(false);

    useEffect(() => {
        let interval: any;
        if (simStatus === "connected") {
            interval = setInterval(() => {
                setSimTimer(prev => prev + 1);
            }, 1000);
        } else {
            setSimTimer(0);
        }
        return () => clearInterval(interval);
    }, [simStatus]);

    const formatTimer = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const initiateCall = async () => {
        if (!simPhone.trim() || !simProductId.trim()) {
            setSimStatus("error");
            setSimStatusText("Phone number and Product ID are required");
            return;
        }

        callAbortedRef.current = false;
        setSimStatus("requesting");
        setSimStatusText("Initiating WebRTC call with backend...");
        setSimMessages([{ sender: "system", text: `POST /samaira/rfq-calls/initiate-webrtc for ${simPhone}`, time: moment().format("HH:mm:ss") }]);

        try {
            // Step 1: POST /samaira/rfq-calls/initiate-webrtc
            const response = await axiosInstance.post(apis.SAMAIRA_INITIATE_WEBRTC, {
                phone_number: simPhone,
                product_id: simProductId,
            });

            // Check if user aborted during the HTTP request
            if (callAbortedRef.current) {
                return;
            }

            const data = response.data;
            if (data.status !== "success" || !data.ws_url) {
                throw new Error(data.message || "Failed to initiate WebRTC call on backend");
            }

            setSimStatus("connecting");
            setSimStatusText("Connecting WebSocket/WebRTC...");
            setSimMessages(prev => [
                ...prev,
                { sender: "system", text: `Context preloaded. WS URL: ${data.ws_url}`, time: moment().format("HH:mm:ss") },
                { sender: "system", text: "Connecting to media stream...", time: moment().format("HH:mm:ss") }
            ]);

            // Step 2: Connect WebSocket/WebRTC via PipecatClient
            const transport = new WebSocketTransport({
                serializer: new ProtobufFrameSerializer(),
            });

            const client = new PipecatClient({
                transport: transport,
                enableMic: true,
                enableCam: false,
                callbacks: {
                    onConnected: () => {
                        // Check if aborted just before connection established
                        if (callAbortedRef.current) {
                            client.disconnect().catch(() => {});
                            return;
                        }
                        setSimStatus("connected");
                        setSimStatusText("Active Call");
                        setSimMessages(prev => [
                            ...prev,
                            { sender: "system", text: "Call connected. Samaira AI is listening.", time: moment().format("HH:mm:ss") }
                        ]);
                    },
                    onDisconnected: () => {
                        setSimStatus("ended");
                        setSimStatusText("Call Ended");
                        setSimMessages(prev => [
                            ...prev,
                            { sender: "system", text: "Call disconnected", time: moment().format("HH:mm:ss") }
                        ]);
                        setActiveClient(null);
                    },
                    onError: (err: any) => {
                        console.error("Pipecat error:", err);
                        // Only set error if not aborted by user
                        if (!callAbortedRef.current) {
                            setSimStatus("error");
                            setSimStatusText(`Error: ${err?.message || err || "Unknown error"}`);
                            setSimMessages(prev => [
                                ...prev,
                                { sender: "system", text: `Connection error: ${err?.message || err}`, time: moment().format("HH:mm:ss") }
                            ]);
                        }
                    }
                }
            });

            // Bind transcripts
            client.on(RTVIEvent.UserTranscript, (transcript: any) => {
                if (transcript && transcript.text) {
                    setSimMessages(prev => [
                        ...prev,
                        { sender: "user", text: transcript.text, time: moment().format("HH:mm:ss") }
                    ]);
                }
            });

            client.on(RTVIEvent.BotTranscript, (transcript: any) => {
                if (transcript && transcript.text) {
                    setSimMessages(prev => [
                        ...prev,
                        { sender: "bot", text: transcript.text, time: moment().format("HH:mm:ss") }
                    ]);
                }
            });

            // Fallback for LLM Text if transcript isn't fired
            client.on(RTVIEvent.BotLlmText, (data: any) => {
                if (data && data.text) {
                    setSimMessages(prev => {
                        const last = prev[prev.length - 1];
                        if (last && last.sender === "bot" && last.text.includes(data.text)) {
                            return prev;
                        }
                        return [
                            ...prev,
                            { sender: "bot", text: data.text, time: moment().format("HH:mm:ss") }
                        ];
                    });
                }
            });

            // Speaking indicators
            client.on(RTVIEvent.BotStartedSpeaking, () => setIsBotSpeaking(true));
            client.on(RTVIEvent.BotStoppedSpeaking, () => setIsBotSpeaking(false));
            client.on(RTVIEvent.UserStartedSpeaking, () => setIsUserSpeaking(true));
            client.on(RTVIEvent.UserStoppedSpeaking, () => setIsUserSpeaking(false));

            // Check if aborted before triggering websocket connect
            if (callAbortedRef.current) {
                return;
            }

            // Start connection
            await client.connect({
                wsUrl: data.ws_url,
            });

            // Check if aborted during websocket connection process
            if (callAbortedRef.current) {
                try {
                    await client.disconnect();
                } catch (e) {}
                return;
            }

            setActiveClient(client);

        } catch (error: any) {
            console.error("Failed to start simulator:", error);
            if (!callAbortedRef.current) {
                setSimStatus("error");
                setSimStatusText(error.message || "Failed to start simulator");
                setSimMessages(prev => [
                    ...prev,
                    { sender: "system", text: `Failed: ${error.message || error}`, time: moment().format("HH:mm:ss") }
                ]);
            }
        }
    };

    const disconnectCall = async () => {
        callAbortedRef.current = true;
        setSimStatus("ended");
        setSimStatusText("Call Hangup");
        setIsBotSpeaking(false);
        setIsUserSpeaking(false);
        setActiveClient(null);

        try {
            await axiosInstance.post(`${apis.SAMAIRA_DISCONNECT_WEBRTC}?phone=${simPhone}`, {
                phone_number: simPhone,
            });
        } catch (e) {
            console.error("Error calling disconnect endpoint:", e);
        }
    };

    const toggleMute = () => {
        if (activeClient) {
            try {
                const nextMute = !isMuted;
                activeClient.enableMic(!nextMute);
                setIsMuted(nextMute);
                setSimMessages(prev => [
                    ...prev,
                    { sender: "system", text: nextMute ? "Microphone muted" : "Microphone unmuted", time: moment().format("HH:mm:ss") }
                ]);
            } catch (e: any) {
                console.error("Mute error:", e);
            }
        }
    };

    const openSimulatorWithDetails = (phone: string, msn: string) => {
        setSimPhone(phone || "9415332242");
        setSimProductId(msn || "MSNO5WN3316R51");
        setSimStatus("idle");
        setSimStatusText("Ready to start call simulation");
        setSimMessages([]);
        setSimulatorOpen(true);
    };

    const fetchRecords = async (
        page = 1,
        limit = 10,
        phone = phoneFilter,
        from: Dayjs | null = fromDate,
        to: Dayjs | null = toDate,
    ) => {
        setLoading(true);
        try {
            const params: any = { page, limit };
            if (phone) params.phone = phone;
            if (from) params.from_date = from.format("DD-MM-YYYY");
            if (to) params.to_date = to.format("DD-MM-YYYY");
            const response = await axiosInstance.get(apis.SAMAIRA_RFQ_CALLS, { params });
            const data = response.data;
            if (data.status === "success") {
                setRecords(data.data || []);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error("Error fetching Samaira RFQ calls:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAnalytics = async (
        from: Dayjs | null = fromDate,
        to: Dayjs | null = toDate,
    ) => {
        setAnalyticsLoading(true);
        try {
            const start = from ? from.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
            const end = to ? to.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
            const response = await axiosInstance.get(apis.SAMAIRA_RFQ_CALL_ANALYTICS, {
                params: { start_date: start, end_date: end }
            });
            if (response.data && response.data.status) {
                setAnalytics(response.data);
            } else {
                setAnalytics(null);
            }
        } catch (error) {
            console.error("Error fetching call analytics:", error);
            setAnalytics(null);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const applyQuickFilter = (label: string, days: number) => {
        let from: Dayjs;
        let to: Dayjs;
        if (days === 0) {
            // Today
            from = dayjs().startOf("day");
            to = dayjs().endOf("day");
        } else if (days === 1) {
            // Yesterday
            from = dayjs().subtract(1, "day").startOf("day");
            to = dayjs().subtract(1, "day").endOf("day");
        } else {
            from = dayjs().subtract(days - 1, "day").startOf("day");
            to = dayjs().endOf("day");
        }
        setFromDate(from);
        setToDate(to);
        setActiveQuick(label);
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchRecords(1, pagination.limit, phoneFilter, from, to);
        fetchAnalytics(from, to);
    };

    useEffect(() => {
        fetchRecords(1, 10, "");
        fetchAnalytics(null, null);
        // eslint-disable-next-line
    }, []);

    const handleTableChange = (pag: TablePaginationConfig) => {
        fetchRecords(pag.current, pag.pageSize, phoneFilter, fromDate, toDate);
    };

    const handleSearch = () => {
        setPhoneFilter(inputPhone);
        setActiveQuick(null);
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchRecords(1, pagination.limit, inputPhone, fromDate, toDate);
        fetchAnalytics(fromDate, toDate);
    };

    const handleReset = () => {
        setInputPhone("");
        setPhoneFilter("");
        setFromDate(null);
        setToDate(null);
        setActiveQuick(null);
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchRecords(1, pagination.limit, "", null, null);
        fetchAnalytics(null, null);
    };

    const handleViewDetails = (record: any) => {
        setSelectedRecord(record);
        setDrawerOpen(true);
    };

    const columns = [
        {
            title: "User",
            key: "user",
            width: 220,
            render: (_: any, record: any) => {
                const user = record.user_details;
                const rfqName = record.active_rfqs?.rfqs?.[0]?.customer_name;
                const name = user?.name || rfqName || record.customer_name || null;
                const email = user?.email || record.active_rfqs?.rfqs?.[0]?.email || null;
                const customerId = user?.customer_id || null;
                return (
                    <div className="flex flex-col gap-0.5">
                        {/* Phone */}
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <PhoneOutlined style={{ fontSize: 10, color: "#6b7280" }} />
                            <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#111827" }}>
                                {record.phone}
                            </span>
                        </div>
                        {/* Meta below */}
                        {name && (
                            <span style={{ fontSize: 11, color: "#374151", fontWeight: 500 }}>{name}</span>
                        )}
                        {email && (
                            <span style={{ fontSize: 10, color: "#9ca3af", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {email}
                            </span>
                        )}
                        {customerId && (
                            <span style={{ fontSize: 9, color: "#d1d5db", fontFamily: "monospace" }}>
                                CID: {customerId}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Active RFQs",
            key: "rfqs",
            width: 160,
            render: (_: any, record: any) => {
                const rfq = record.active_rfqs;
                if (!rfq?.active_rfqs_found) return <Tag color="default">None</Tag>;
                const all = rfq.rfqs || [];
                const visible = all.slice(0, 2);
                const extra = all.length - visible.length;
                return (
                    <div className="flex flex-col" style={{ gap: 4 }}>
                        {visible.map((r: any, i: number) => (
                            <div key={r.rfq_id} style={{ paddingTop: i > 0 ? 4 : 0, borderTop: i > 0 ? "1px solid #f3f4f6" : "none" }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "monospace" }}>
                                    #{r.rfq_id}
                                </span>
                                {r.created_date && (
                                    <div style={{ fontSize: 10, color: "#9ca3af" }}>{r.created_date}</div>
                                )}
                            </div>
                        ))}
                        {extra > 0 && (
                            <span style={{ fontSize: 10, color: "#9ca3af" }}>+{extra} more</span>
                        )}
                    </div>
                );
            },
        },

        {
            title: "Current Product",
            key: "current_product",
            width: 140,
            render: (_: any, record: any) => {
                const msn = record.current_product?.msn || record.msn;
                return msn ? (
                    <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "monospace", color: "#111827" }}>
                        {msn}
                    </span>
                ) : (
                    <span className="text-gray-300">—</span>
                );
            },
        },

        {
            title: "Best Matches",
            key: "best_matches",
            width: 120,
            render: (_: any, record: any) => {
                const bm = record.best_matches;
                return bm?.products_found ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                        {bm.total_matches} found
                    </Tag>
                ) : (
                    <Tag color="red" icon={<CloseCircleOutlined />}>None</Tag>
                );
            },
        },
        {
            title: "Orders",
            key: "orders",
            width: 100,
            render: (_: any, record: any) => {
                const orders = record.order_history || [];
                return orders.length > 0 ? (
                    <Badge count={orders.length} style={{ backgroundColor: '#8b5cf6' }} overflowCount={99} />
                ) : (
                    <span className="text-gray-300 text-xs">None</span>
                );
            },
        },
        {
            title: "Called At",
            dataIndex: "_timestamp",
            key: "timestamp",
            width: 140,
            render: (ts: number) => {
                if (!ts) return <span className="text-gray-300">—</span>;
                const m = moment.unix(ts);
                return (
                    <div className="flex flex-col">
                        <span className="text-xs font-medium text-gray-700">{m.format("MMM DD, YYYY")}</span>
                        <span className="text-[10px] text-gray-400">{m.format("HH:mm:ss")}</span>
                    </div>
                );
            },
        },
        {
            title: "",
            key: "action",
            width: 120,
            fixed: "right" as const,
            render: (_: any, record: any) => (
                <Button
                    type="primary"
                    ghost
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(record);
                    }}
                    className="text-xs"
                >
                    View
                </Button>
            ),
        },
    ];

    // ---- Drawer Detail Sections ----
    const renderRfqItems = (items: any[]) =>
        items.map((item: any, i: number) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                {item.image_url && (
                    <Image
                        src={item.image_url}
                        width={48}
                        height={48}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                        preview={false}
                        fallback="https://via.placeholder.com/48"
                    />
                )}
                <div className="flex-1 min-w-0">
                    <a href={item.product_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs font-medium text-blue-600 hover:underline line-clamp-2">
                        {item.product_name}
                    </a>
                    <div className="flex gap-3 mt-1 flex-wrap">
                        <span className="text-[10px] text-gray-400 font-mono">{item.msn}</span>
                        <span className="text-[10px] text-gray-500">Brand: {item.brand}</span>
                        <span className="text-[10px] text-gray-500">Qty: {item.quantity}</span>
                        <span className="text-[10px] text-gray-700 font-semibold">₹{item.price}</span>
                    </div>
                </div>
            </div>
        ));

    const renderBestMatches = (products: any[]) =>
        products.map((p: any, i: number) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                {p.main_image && (
                    <Image
                        src={p.main_image}
                        width={48}
                        height={48}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                        preview={false}
                        fallback="https://via.placeholder.com/48"
                    />
                )}
                <div className="flex-1 min-w-0">
                    <a href={p.product_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs font-medium text-blue-600 hover:underline line-clamp-2">
                        {p.product_name}
                    </a>
                    <div className="flex gap-3 mt-1 flex-wrap">
                        <span className="text-[10px] font-mono text-gray-400">{p.moglix_part_number}</span>
                        <span className="text-[10px] text-gray-500">{p.brand_name}</span>
                        <span className="text-[10px] font-bold text-green-700">₹{p.sales_price}</span>
                        {p.discount_percent > 0 && (
                            <Tag color="green" style={{ fontSize: 9, lineHeight: '14px', padding: '0 4px' }}>
                                {p.discount_percent}% off
                            </Tag>
                        )}
                        <Tag color={p.in_stock ? "green" : "red"} style={{ fontSize: 9, lineHeight: '14px', padding: '0 4px' }}>
                            {p.in_stock ? "In Stock" : "OOS"}
                        </Tag>
                    </div>
                </div>
            </div>
        ));

    const renderOrderHistory = (orders: any[]) =>
        orders.map((o: any, i: number) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                {o.image_url?.trim() && (
                    <Image
                        src={o.image_url}
                        width={40}
                        height={40}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                        preview={false}
                        fallback="https://via.placeholder.com/40"
                    />
                )}
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{o.product_name}</p>
                    <div className="flex gap-3 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-gray-500">Order #{o.order_id}</span>
                        <Tag color={o.item_status === "CANCELLED" ? "red" : "green"}
                            style={{ fontSize: 9, lineHeight: '14px', padding: '0 4px' }}>
                            {o.item_status}
                        </Tag>
                        <span className="text-[10px] text-gray-700 font-semibold">₹{o.price}</span>
                        <span className="text-[10px] text-gray-400">{o.order_date}</span>
                    </div>
                </div>
            </div>
        ));

    return (
        <StitchLayout>
            <div className="space-y-6 max-w-[1600px] mx-auto">
                {/* Page Header */}
                <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 text-2xl">
                            <PhoneOutlined />
                        </div>
                        <div>
                            <Title level={3} style={{ marginBottom: 0, letterSpacing: '-0.5px' }}>
                                Samaira — RFQ Call Tracker
                            </Title>
                            <Text type="secondary">Calls made to users who raised RFQs · powered by Samaira AI</Text>
                        </div>
                    </div>
                    <Space>
                        <Button
                            icon={<PhoneOutlined />}
                            onClick={() => openSimulatorWithDetails("9415332242", "MSNO5WN3316R51")}
                            className="font-medium hover:bg-orange-50 transition-all duration-200"
                            style={{ background: "#fff7ed", borderColor: "#fed7aa", color: "#ea580c" }}
                        >
                            Call Simulator
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={() => fetchRecords(pagination.page, pagination.limit)}>
                            Refresh
                        </Button>
                    </Space>
                </div>

                {/* Filter Bar */}
                <Card bordered={false} className="shadow-sm rounded-xl">
                    <div className="flex flex-wrap items-end gap-4">
                        {/* Phone */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500 font-medium">Phone Number</span>
                            <Input
                                placeholder="e.g. 9415332242"
                                prefix={<PhoneOutlined className="text-gray-400" />}
                                value={inputPhone}
                                onChange={e => setInputPhone(e.target.value)}
                                onPressEnter={handleSearch}
                                style={{ width: 200 }}
                                allowClear
                            />
                        </div>

                        {/* From Date */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500 font-medium">From Date</span>
                            <DatePicker
                                value={fromDate}
                                onChange={val => { setFromDate(val); setActiveQuick(null); }}
                                format="DD-MM-YYYY"
                                placeholder="DD-MM-YYYY"
                                style={{ width: 150 }}
                                allowClear
                                disabledDate={cur => toDate ? cur.isAfter(toDate, "day") : false}
                            />
                        </div>

                        {/* To Date */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500 font-medium">To Date</span>
                            <DatePicker
                                value={toDate}
                                onChange={val => { setToDate(val); setActiveQuick(null); }}
                                format="DD-MM-YYYY"
                                placeholder="DD-MM-YYYY"
                                style={{ width: 150 }}
                                allowClear
                                disabledDate={cur => fromDate ? cur.isBefore(fromDate, "day") : false}
                            />
                        </div>

                        <Space>
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
                                Search
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleReset}>
                                Reset
                            </Button>
                        </Space>
                    </div>

                    {/* Quick Filter Strip */}
                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-400 font-medium mr-1">Quick:</span>
                        {QUICK_FILTERS.map(({ label, days }) => {
                            const isActive = activeQuick === label;
                            return (
                                <button
                                    key={label}
                                    onClick={() => applyQuickFilter(label, days)}
                                    style={{
                                        fontSize: 12,
                                        padding: "3px 12px",
                                        borderRadius: 20,
                                        border: isActive ? "1.5px solid #f97316" : "1.5px solid #e5e7eb",
                                        background: isActive ? "#fff7ed" : "#f9fafb",
                                        color: isActive ? "#ea580c" : "#6b7280",
                                        fontWeight: isActive ? 600 : 400,
                                        cursor: "pointer",
                                        transition: "all 0.15s",
                                        lineHeight: "20px",
                                    }}
                                >
                                    {label}
                                </button>
                            );
                        })}
                        {activeQuick && (
                            <button
                                onClick={handleReset}
                                style={{
                                    fontSize: 11,
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    border: "1.5px solid #fca5a5",
                                    background: "#fef2f2",
                                    color: "#dc2626",
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                    lineHeight: "20px",
                                }}
                            >
                                ✕ Clear
                            </button>
                        )}
                    </div>
                </Card>

                {/* Modern Horizontal Tabs */}
                <div className="flex items-center gap-1 bg-gray-100/90 p-1.5 rounded-2xl max-w-md shadow-xs border border-gray-200/50">
                    <button
                        onClick={() => setActiveTab("analytics")}
                        style={{ outline: "none" }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-xs font-bold transition-all duration-200 border-none cursor-pointer ${
                            activeTab === "analytics"
                                ? "bg-white text-orange-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-700 hover:bg-white/30"
                        }`}
                    >
                        📊 Performance Analytics
                    </button>
                    <button
                        onClick={() => setActiveTab("calls")}
                        style={{ outline: "none" }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-xs font-bold transition-all duration-200 border-none cursor-pointer ${
                            activeTab === "calls"
                                ? "bg-white text-orange-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-700 hover:bg-white/30"
                        }`}
                    >
                        📞 Call Records List
                    </button>
                </div>

                {activeTab === "analytics" ? (
                    /* SAMAIRA RFQ Call Analytics Section */
                    <Card bordered={false} className="shadow-sm rounded-xl border border-gray-100 bg-white overflow-hidden animate-[fadeIn_0.25s_ease-out]">
                        {/* Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-4 mb-6 border-b border-gray-100 gap-4">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 bg-orange-500 rounded-full animate-ping" />
                                    <h4 className="text-lg font-bold text-gray-900 m-0">Samaira Call & Chat Analytics</h4>
                                    <Tag color="orange" className="font-semibold text-[10px] rounded-full px-2 ml-1">Live Integration</Tag>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                    Real-time conversion funnel and engagement stats driven by date filters
                                    {fromDate || toDate ? (
                                        <span className="font-semibold text-gray-600 ml-1">
                                            ({fromDate ? fromDate.format("DD MMM YYYY") : "Start"} to {toDate ? toDate.format("DD MMM YYYY") : "End"})
                                        </span>
                                    ) : (
                                        <span className="font-semibold text-gray-500 ml-1">(Today: {dayjs().format("DD MMM YYYY")})</span>
                                    )}
                                </p>
                            </div>
                            {analytics?.last_interaction_at && (
                                <div className="text-[11px] text-gray-400 bg-gray-50 border border-gray-100 rounded-lg px-3 py-1.5 font-mono">
                                    Last call made to user: <span className="font-semibold text-gray-600">{analytics.last_interaction_at}</span>
                                </div>
                            )}
                        </div>

                        {analyticsLoading ? (
                            /* Beautiful skeleton loading state */
                            <div className="space-y-6 animate-pulse">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    {[1, 2, 3, 4].map(i => (
                                        <div key={i} className="h-24 bg-gray-100 rounded-xl" />
                                    ))}
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                    <div className="lg:col-span-7 h-64 bg-gray-100 rounded-xl" />
                                    <div className="lg:col-span-5 h-64 bg-gray-100 rounded-xl" />
                                </div>
                            </div>
                        ) : analytics ? (
                            <div className="space-y-6">
                                {/* Funnel KPI Cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {[
                                        {
                                            title: "Total Calls So Far",
                                            value: analytics.total_calls ?? 0,
                                            sub: "Cumulative outbound calls",
                                            gradient: "from-blue-500 to-indigo-600",
                                            icon: <PhoneOutlined className="text-white text-xl opacity-90" />,
                                        },
                                        {
                                            title: "RFQs Raised",
                                            value: analytics.rfq_raised_count ?? 0,
                                            sub: "In selected time range",
                                            gradient: "from-emerald-500 to-teal-600",
                                            icon: <ShoppingOutlined className="text-white text-xl opacity-90" />,
                                        },
                                        {
                                            title: "Target RFQ Users Reached",
                                            value: analytics.filtered_count ?? 0,
                                            sub: "Outbound calls in range",
                                            gradient: "from-purple-500 to-violet-600",
                                            icon: <UserOutlined className="text-white text-xl opacity-90" />,
                                        },
                                        {
                                            title: "Checkout Links Sent",
                                            value: analytics.notification_analytics?.sent_count ?? 0,
                                            sub: `Template: ${analytics.notification_analytics?.template_name || "N/A"}`,
                                            gradient: "from-orange-500 to-amber-600",
                                            icon: <LinkOutlined className="text-white text-xl opacity-90" />,
                                        },
                                    ].map((card, i) => (
                                        <div 
                                            key={i} 
                                            className={`relative overflow-hidden rounded-xl p-4 bg-gradient-to-br ${card.gradient} text-white shadow-sm hover:scale-[1.02] transition-all duration-200`}
                                            style={{ boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.05)" }}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="space-y-1">
                                                    <span className="text-[11px] font-medium opacity-80 uppercase tracking-wider">{card.title}</span>
                                                    <h3 className="text-2xl font-black text-white m-0 tracking-tight">{card.value.toLocaleString()}</h3>
                                                </div>
                                                <div className="p-2 bg-white/10 rounded-lg backdrop-blur-md">
                                                    {card.icon}
                                                </div>
                                            </div>
                                            <div className="text-[10px] opacity-75 mt-3 pt-2 border-t border-white/10 flex items-center justify-between">
                                                <span>{card.sub}</span>
                                                {i === 2 && analytics.rfq_raised_count > 0 && (
                                                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-[9px] font-bold">
                                                        {Math.round((analytics.filtered_count / analytics.rfq_raised_count) * 100)}% reach
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Main Breakdown Section */}
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                    {/* Left Panel: Call Status & Performance */}
                                    <div className="lg:col-span-7 border border-gray-100 rounded-xl p-5 bg-gray-50/50">
                                        <div className="flex justify-between items-center mb-4">
                                            <div className="flex items-center gap-2">
                                                <PhoneOutlined className="text-orange-500" />
                                                <span className="font-bold text-gray-800 text-sm">Call Connection Breakdown</span>
                                            </div>
                                            <Tag color="orange" className="m-0 text-[10px] font-semibold">
                                                {analytics.call_status_breakdown?.length || 0} Statuses
                                            </Tag>
                                        </div>

                                        {/* Special Answered Metrics block */}
                                        {(() => {
                                            const answered = analytics.call_status_breakdown?.find((x: any) => x.status?.toLowerCase() === "answered");
                                            if (answered) {
                                                const secGte50Rate = answered.count > 0 ? Math.round((answered.duration_gte_50_sec / answered.count) * 100) : 0;
                                                return (
                                                    <div className="grid grid-cols-3 gap-3 mb-4 bg-white border border-gray-100 rounded-xl p-3 shadow-xs">
                                                        <div className="text-center">
                                                            <p className="text-[9px] text-gray-400 font-semibold uppercase">Answered Count</p>
                                                            <p className="text-lg font-black text-emerald-600 m-0">{answered.count}</p>
                                                        </div>
                                                        <div className="text-center border-x border-gray-100">
                                                            <p className="text-[9px] text-gray-400 font-semibold uppercase">Avg Duration</p>
                                                            <p className="text-lg font-black text-gray-700 m-0">{answered.avg_call_duration || "—"}</p>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-[9px] text-gray-400 font-semibold uppercase">Engaged (&ge;50s)</p>
                                                            <p className="text-lg font-black text-indigo-600 m-0">
                                                                {answered.duration_gte_50_sec} <span className="text-[10px] text-gray-400 font-normal">({secGte50Rate}%)</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        {/* Breakdown Chart using Recharts */}
                                        <div className="flex flex-col sm:flex-row items-center gap-6 mt-4">
                                            {/* Column 1: Donut PieChart */}
                                            {(() => {
                                                const totalCalls = analytics.call_status_breakdown?.reduce((acc: number, item: any) => acc + (item.count || 0), 0) || 0;
                                                const chartData = analytics.call_status_breakdown?.map((item: any) => ({
                                                    name: item.status || "Unknown",
                                                    value: item.count || 0,
                                                })) || [];

                                                const STATUS_COLORS: { [key: string]: string } = {
                                                    answered: "#10b981", // Success green
                                                    normalunspecified: "#3b82f6", // Blue
                                                    noresponse: "#f59e0b", // Amber
                                                    interworkingunspecified: "#6366f1", // Indigo
                                                    invalidnumber: "#ef4444", // Red
                                                    rejected: "#ec4899", // Pink
                                                    noroutedestination: "#8b5cf6", // Purple
                                                    noanswer: "#f97316", // Orange
                                                };

                                                const getColor = (statusName: string) => {
                                                    const key = statusName.toLowerCase().replace(/\s+/g, "");
                                                    return STATUS_COLORS[key] || "#94a3b8"; // Fallback grey
                                                };

                                                return (
                                                    <div className="relative w-40 h-40 flex items-center justify-center bg-white rounded-xl border border-gray-100 p-2 shadow-xs flex-shrink-0 mx-auto sm:mx-0">
                                                        <div className="w-full h-full">
                                                            <ResponsiveContainer width="100%" height="100%">
                                                                <PieChart>
                                                                    <Pie
                                                                        data={chartData}
                                                                        cx="50%"
                                                                        cy="50%"
                                                                        innerRadius={46}
                                                                        outerRadius={64}
                                                                        paddingAngle={3}
                                                                        dataKey="value"
                                                                    >
                                                                        {chartData.map((entry: any, index: number) => (
                                                                            <Cell key={`cell-${index}`} fill={getColor(entry.name)} />
                                                                        ))}
                                                                    </Pie>
                                                                    <ChartTooltip
                                                                        content={({ active, payload }) => {
                                                                            if (active && payload && payload.length) {
                                                                                const data = payload[0].payload;
                                                                                const pct = totalCalls > 0 ? Math.round((data.value / totalCalls) * 100) : 0;
                                                                                return (
                                                                                    <div className="bg-slate-900 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold shadow-md border border-slate-800">
                                                                                        <span className="capitalize">{data.name}</span>
                                                                                        <p className="m-0 text-[10px] text-gray-300 font-mono mt-0.5">
                                                                                            Calls: <span className="text-white font-bold">{data.value}</span> ({pct}%)
                                                                                        </p>
                                                                                    </div>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        }}
                                                                    />
                                                                </PieChart>
                                                            </ResponsiveContainer>
                                                        </div>
                                                        <div className="absolute flex flex-col items-center justify-center text-center pointer-events-none">
                                                            <span className="text-xl font-black text-gray-800 leading-none">{totalCalls}</span>
                                                            <span className="text-[8px] text-gray-400 uppercase font-semibold mt-1">Total</span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* Column 2: Sleek Legend List */}
                                            <div className="flex-1 space-y-2.5 w-full max-h-[200px] overflow-y-auto pr-1">
                                                {(() => {
                                                    const totalBreakdown = analytics.call_status_breakdown?.reduce((acc: number, item: any) => acc + (item.count || 0), 0) || 1;
                                                    const STATUS_COLORS: { [key: string]: string } = {
                                                        answered: "#10b981",
                                                        normalunspecified: "#3b82f6",
                                                        noresponse: "#f59e0b",
                                                        interworkingunspecified: "#6366f1",
                                                        invalidnumber: "#ef4444",
                                                        rejected: "#ec4899",
                                                        noroutedestination: "#8b5cf6",
                                                        noanswer: "#f97316",
                                                    };
                                                    const getColor = (statusName: string) => {
                                                        const key = statusName.toLowerCase().replace(/\s+/g, "");
                                                        return STATUS_COLORS[key] || "#94a3b8";
                                                    };

                                                    return analytics.call_status_breakdown?.map((item: any, idx: number) => {
                                                        const pct = Math.round((item.count / totalBreakdown) * 100);
                                                        const isAnswered = item.status?.toLowerCase() === "answered";
                                                        const color = getColor(item.status);

                                                        return (
                                                            <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-100/50 hover:bg-gray-100/30 px-1 rounded transition-colors">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                                                    <span className="font-semibold text-gray-700 capitalize truncate max-w-[140px]">{item.status || "Unknown"}</span>
                                                                    {isAnswered && <span className="text-[9px] bg-emerald-50 text-emerald-600 border border-emerald-100 rounded px-1 flex-shrink-0">Success</span>}
                                                                </div>
                                                                <div className="font-mono text-gray-500 flex items-center gap-1.5 flex-shrink-0">
                                                                    <span className="font-bold text-gray-800">{item.count}</span>
                                                                    <span className="text-[10px] text-gray-400">({pct}%)</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Panel: Checkout Link Conversion Funnel */}
                                    <div className="lg:col-span-5 border border-gray-100 rounded-xl p-5 bg-gray-50/50 flex flex-col justify-between">
                                        <div>
                                            <div className="flex justify-between items-center mb-4">
                                                <div className="flex items-center gap-2">
                                                    <LinkOutlined className="text-orange-500" />
                                                    <span className="font-bold text-gray-800 text-sm">Checkout Link Engagement</span>
                                                </div>
                                                <Tag color="orange" className="m-0 text-[10px] font-semibold">WhatsApp Funnel</Tag>
                                            </div>

                                            {/* Sent vs Read Conversion Circle */}
                                            {(() => {
                                                const sent = analytics.notification_analytics?.sent_count || 0;
                                                const read = analytics.notification_analytics?.status_breakdown?.read || 0;
                                                const readRate = sent > 0 ? Math.round((read / sent) * 100) : 0;
                                                
                                                // Circular SVG computations
                                                const radius = 38;
                                                const strokeWidth = 8;
                                                const circumference = 2 * Math.PI * radius;
                                                const strokeDashoffset = circumference - (readRate / 100) * circumference;

                                                return (
                                                    <div className="flex flex-col sm:flex-row items-center gap-6 bg-white border border-gray-100 rounded-xl p-4 shadow-xs mb-4">
                                                        {/* SVG Gauge */}
                                                        <div className="relative w-24 h-24 flex items-center justify-center">
                                                            <svg className="w-full h-full transform -rotate-90">
                                                                {/* Track */}
                                                                <circle
                                                                    cx="48"
                                                                    cy="48"
                                                                    r={radius}
                                                                    className="text-gray-100"
                                                                    strokeWidth={strokeWidth}
                                                                    stroke="currentColor"
                                                                    fill="transparent"
                                                                />
                                                                {/* Filled bar */}
                                                                <circle
                                                                    cx="48"
                                                                    cy="48"
                                                                    r={radius}
                                                                    className="text-orange-500 transition-all duration-1000 ease-out"
                                                                    strokeWidth={strokeWidth}
                                                                    strokeDasharray={circumference}
                                                                    strokeDashoffset={strokeDashoffset}
                                                                    strokeLinecap="round"
                                                                    stroke="currentColor"
                                                                    fill="transparent"
                                                                />
                                                            </svg>
                                                            <div className="absolute flex flex-col items-center justify-center text-center">
                                                                <span className="text-lg font-black text-gray-800 tracking-tighter leading-none">{readRate}%</span>
                                                                <span className="text-[8px] text-gray-400 uppercase font-semibold mt-0.5">Read Rate</span>
                                                            </div>
                                                        </div>

                                                        {/* Quick Stat Details */}
                                                        <div className="flex-1 space-y-2">
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className="text-gray-400 font-medium">Checkout Links Shared</span>
                                                                <span className="font-bold text-gray-800">{sent}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className="text-gray-400 font-medium">Successfully Read</span>
                                                                <span className="font-bold text-emerald-600">{read}</span>
                                                            </div>
                                                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden mt-1 flex">
                                                                <div className="bg-emerald-500 h-full" style={{ width: `${sent > 0 ? (read / sent) * 100 : 0}%` }} />
                                                                <div className="bg-blue-400 h-full" style={{ width: `${sent > 0 ? ((analytics.notification_analytics?.status_breakdown?.delivered || 0) / sent) * 100 : 0}%` }} />
                                                                <div className="bg-amber-400 h-full" style={{ width: `${sent > 0 ? ((analytics.notification_analytics?.status_breakdown?.sent || 0) / sent) * 100 : 0}%` }} />
                                                                <div className="bg-red-400 h-full" style={{ width: `${sent > 0 ? ((analytics.notification_analytics?.status_breakdown?.failed || 0) / sent) * 100 : 0}%` }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Failed or Empty State */
                            <div className="py-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                <InfoCircleOutlined style={{ fontSize: 24, color: "#d1d5db" }} />
                                <p className="text-sm text-gray-400 font-medium mt-2">No analytics data available for this range</p>
                                <Button size="small" type="primary" className="mt-2" onClick={() => fetchAnalytics(fromDate, toDate)}>
                                    Retry
                                </Button>
                            </div>
                        )}
                    </Card>
                ) : (
                    /* SAMAIRA RFQ Call Records & Table Section */
                    <div className="space-y-6 animate-[fadeIn_0.25s_ease-out]">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                {
                                    label: "Total Records",
                                    value: pagination.total_records,
                                    color: "bg-blue-50 border-blue-200 text-blue-700",
                                    icon: <UserOutlined />,
                                },
                                {
                                    label: "With Active RFQs",
                                    value: records.filter(r => r.active_rfqs?.active_rfqs_found).length,
                                    color: "bg-green-50 border-green-200 text-green-700",
                                    icon: <ShoppingOutlined />,
                                },
                                {
                                    label: "With Best Matches",
                                    value: records.filter(r => r.best_matches?.products_found).length,
                                    color: "bg-purple-50 border-purple-200 text-purple-700",
                                    icon: <StarOutlined />,
                                },
                                {
                                    label: "With Orders",
                                    value: records.filter(r => r.order_history?.length > 0).length,
                                    color: "bg-orange-50 border-orange-200 text-orange-700",
                                    icon: <CheckCircleOutlined />,
                                },
                            ].map(stat => (
                                <div key={stat.label} className={`flex items-center gap-3 border rounded-xl px-4 py-3 ${stat.color}`}>
                                    <div className="text-xl">{stat.icon}</div>
                                    <div>
                                        <div className="text-xl font-bold">{stat.value}</div>
                                        <div className="text-xs font-medium opacity-75">{stat.label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Table */}
                        <Card bordered={false} className="shadow-sm rounded-xl overflow-hidden" styles={{ body: { padding: 0 } }}>
                            <Table
                                columns={columns as any}
                                dataSource={records}
                                loading={loading}
                                rowKey="_id"
                                pagination={{
                                    current: pagination.page,
                                    pageSize: pagination.limit,
                                    total: pagination.total_records,
                                    showSizeChanger: true,
                                    pageSizeOptions: ["10", "20", "50"],
                                    showTotal: (total, range) => (
                                        <span className="text-gray-500 mx-4">{`${range[0]}-${range[1]} of ${total} records`}</span>
                                    ),
                                    className: "p-4",
                                }}
                                onChange={handleTableChange}
                                onRow={(record: any) => ({
                                    onClick: () => handleViewDetails(record),
                                    style: { cursor: "pointer" },
                                })}
                                scroll={{ x: "max-content" }}
                                size="middle"
                                locale={{
                                    emptyText: <Empty description="No RFQ call records found" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                                }}
                            />
                        </Card>
                    </div>
                )}
            </div>

            {/* Detail Drawer */}
            <Drawer
                title={
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">
                            <PhoneOutlined />
                        </div>
                        <div>
                            <div className="font-semibold text-gray-900">Call Record Details</div>
                            <div className="text-xs text-gray-400 font-mono">{selectedRecord?.phone}</div>
                        </div>
                    </div>
                }
                open={drawerOpen}
                onClose={() => { setDrawerOpen(false); setSelectedRecord(null); }}
                width={640}
                destroyOnClose
            >
                {selectedRecord && (
                    <div className="space-y-4">

                        {/* User Info */}
                        <Card size="small" bordered className="rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                    <UserOutlined />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-800">
                                        {selectedRecord.user_details?.name || selectedRecord.active_rfqs?.rfqs?.[0]?.customer_name || "Unknown"}
                                    </p>
                                    <p className="text-xs text-gray-400">{selectedRecord.user_details?.email || selectedRecord.active_rfqs?.rfqs?.[0]?.email || "—"}</p>
                                    <p className="text-xs text-gray-500 font-mono">{selectedRecord.phone}</p>
                                </div>
                                {selectedRecord.user_details?.customer_id && (
                                    <div className="ml-auto">
                                        <Tag color="blue">CID: {selectedRecord.user_details.customer_id}</Tag>
                                    </div>
                                )}
                            </div>
                            <Divider className="my-2" />
                            <div className="flex gap-4 text-xs text-gray-500">
                                <span>Called: <b className="text-gray-700">{selectedRecord._timestamp ? moment.unix(selectedRecord._timestamp).format("MMM DD, YYYY HH:mm") : "—"}</b></span>
                                <span>Updated: <b className="text-gray-700">{selectedRecord.mongo_updated_at ? moment(selectedRecord.mongo_updated_at).format("MMM DD, YYYY HH:mm") : "—"}</b></span>
                            </div>
                        </Card>

                        {/* Active RFQs */}
                        {selectedRecord.active_rfqs?.active_rfqs_found && (
                            <Collapse defaultActiveKey={["rfqs"]} bordered={false} className="rounded-lg overflow-hidden">
                                <Panel
                                    key="rfqs"
                                    header={
                                        <div className="flex items-center gap-2">
                                            <ShoppingOutlined className="text-blue-500" />
                                            <span className="font-semibold text-gray-800">Active RFQs</span>
                                            <Badge count={selectedRecord.active_rfqs.total_active} style={{ backgroundColor: '#3b82f6' }} />
                                        </div>
                                    }
                                >
                                    <div className="space-y-4">
                                        {selectedRecord.active_rfqs.rfqs.map((rfq: any) => (
                                            <div key={rfq.rfq_id} className="border border-gray-100 rounded-lg p-3">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Tag color="blue">RFQ #{rfq.rfq_id}</Tag>
                                                    <span className="text-[10px] text-gray-400">{rfq.created_date}</span>
                                                </div>
                                                <div className="text-xs text-gray-500 mb-2">
                                                    {[rfq.city, rfq.state, rfq.pincode].filter(Boolean).join(", ") || "Location not set"}
                                                </div>
                                                {renderRfqItems(rfq.items || [])}
                                            </div>
                                        ))}
                                    </div>
                                </Panel>
                            </Collapse>
                        )}

                        {/* Best Matches */}
                        {selectedRecord.best_matches?.products_found && (
                            <Collapse defaultActiveKey={["matches"]} bordered={false} className="rounded-lg overflow-hidden">
                                <Panel
                                    key="matches"
                                    header={
                                        <div className="flex items-center gap-2">
                                            <StarOutlined className="text-green-500" />
                                            <span className="font-semibold text-gray-800">Best Matches</span>
                                            <Badge count={selectedRecord.best_matches.total_matches} style={{ backgroundColor: '#22c55e' }} />
                                        </div>
                                    }
                                >
                                    {renderBestMatches(selectedRecord.best_matches.products || [])}
                                </Panel>
                            </Collapse>
                        )}

                        {/* Current Product */}
                        {selectedRecord.current_product?.success && (
                            <Collapse bordered={false} className="rounded-lg overflow-hidden">
                                <Panel
                                    key="current"
                                    header={
                                        <div className="flex items-center gap-2">
                                            <InfoCircleOutlined className="text-purple-500" />
                                            <span className="font-semibold text-gray-800">Current Product</span>
                                        </div>
                                    }
                                >
                                    <div className="flex items-start gap-3">
                                        {selectedRecord.current_product?.product_group?.productAllImages?.[0]?.links?.large && (
                                            <Image
                                                src={`https://cdn.moglix.com/${selectedRecord.current_product.product_group.productAllImages[0].links.large}`}
                                                width={64}
                                                height={64}
                                                style={{ objectFit: 'cover', borderRadius: 8 }}
                                                preview={false}
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 line-clamp-2">
                                                {selectedRecord.current_product.product_name}
                                            </p>
                                            <p className="text-[10px] font-mono text-gray-400 mt-0.5">{selectedRecord.current_product.msn}</p>
                                            <div className="flex gap-3 mt-1 flex-wrap">
                                                <span className="text-xs font-bold text-gray-800">
                                                    ₹{selectedRecord.current_product.product_group?.productPrice}
                                                </span>
                                                <span className="text-xs text-gray-400 line-through">
                                                    ₹{selectedRecord.current_product.product_group?.productMrp}
                                                </span>
                                                <Tag color={selectedRecord.current_product.product_group?.productOutOfStock ? "red" : "green"} style={{ fontSize: 9 }}>
                                                    {selectedRecord.current_product.product_group?.productOutOfStock ? "Out of Stock" : "In Stock"}
                                                </Tag>
                                            </div>
                                            <a
                                                href={`https://www.moglix.com/${selectedRecord.current_product.product_group?.productUrl}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[10px] text-blue-500 hover:underline flex items-center gap-1 mt-1"
                                            >
                                                <LinkOutlined /> View on Moglix
                                            </a>
                                        </div>
                                    </div>

                                    {/* Product Attributes */}
                                    {selectedRecord.current_product.product_group?.productAttributes && (
                                        <div className="mt-3 grid grid-cols-2 gap-1">
                                            {Object.entries(selectedRecord.current_product.product_group.productAttributes).map(([k, v]: any) => (
                                                <div key={k} className="flex gap-1 text-[10px]">
                                                    <span className="text-gray-400 font-medium">{k}:</span>
                                                    <span className="text-gray-700">{Array.isArray(v) ? v.join(", ") : v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Panel>
                            </Collapse>
                        )}

                        {/* Order History */}
                        {(selectedRecord.order_history?.length > 0) && (
                            <Collapse bordered={false} className="rounded-lg overflow-hidden">
                                <Panel
                                    key="orders"
                                    header={
                                        <div className="flex items-center gap-2">
                                            <CheckCircleOutlined className="text-orange-500" />
                                            <span className="font-semibold text-gray-800">Order History</span>
                                            <Badge count={selectedRecord.order_history.length} style={{ backgroundColor: '#8b5cf6' }} />
                                        </div>
                                    }
                                >
                                    {renderOrderHistory(selectedRecord.order_history)}
                                </Panel>
                            </Collapse>
                        )}

                    </div>
                )}
            </Drawer>

            {/* Call Simulator Modal */}
            <Modal
                title={null}
                footer={null}
                open={simulatorOpen}
                onCancel={() => {
                    disconnectCall();
                    setSimulatorOpen(false);
                }}
                width={420}
                destroyOnClose
                styles={{
                    body: {
                        padding: 0,
                        background: "#0f172a",
                        borderRadius: "24px",
                        overflow: "hidden",
                    }
                }}
                className="simulator-phone-modal"
            >
                <div style={{
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    color: "#f8fafc",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "680px",
                    border: "6px solid #334155",
                    borderRadius: "24px",
                    position: "relative",
                    overflow: "hidden",
                    background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
                }}>
                    {/* Status notch */}
                    <div style={{
                        height: "28px",
                        background: "#020617",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0 20px",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#94a3b8",
                        letterSpacing: "0.5px"
                    }}>
                        <span>{moment().format("HH:mm")}</span>
                        <div style={{
                            width: "70px",
                            height: "15px",
                            background: "#000",
                            borderRadius: "0 0 10px 10px",
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            top: 0
                        }} />
                        <div style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}>
                            <span>5G</span>
                            <span style={{ fontSize: "10px" }}>📶</span>
                            <span>🔋 95%</span>
                        </div>
                    </div>

                    {/* Title / Header of the Call Screen */}
                    <div style={{
                        padding: "24px 20px 12px 20px",
                        textAlign: "center",
                        borderBottom: "1px solid #1e293b",
                        background: "rgba(15, 23, 42, 0.6)",
                        backdropFilter: "blur(8px)"
                    }}>
                        <h3 style={{ margin: 0, color: "#ea580c", fontWeight: 700, fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                            <PhoneOutlined spin={simStatus === "connecting" || simStatus === "requesting"} />
                            Samaira RFQ Simulator
                        </h3>
                        <p style={{ margin: "4px 0 0 0", color: "#94a3b8", fontSize: "11px" }}>
                            Pipecat WebRTC WebSocket Protocol
                        </p>
                    </div>

                    {/* Inputs Panel */}
                    {simStatus === "idle" || simStatus === "error" || simStatus === "ended" ? (
                        <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column", gap: "16px" }}>
                            <div style={{
                                background: "rgba(30, 41, 59, 0.4)",
                                border: "1px solid #334155",
                                borderRadius: "12px",
                                padding: "14px",
                                fontSize: "12px",
                                lineHeight: "1.6",
                                color: "#cbd5e1"
                            }}>
                                <p style={{ margin: "0 0 6px 0", fontWeight: 600, color: "#f97316" }}>🧪 Call Simulator Instructions</p>
                                This simulator triggers a pre-loaded RFQ context for a customer phone number and product MSN, initiates WebRTC on the backend, and routes real-time Protobuf audio frames over a secure WebSocket connection.
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8" }}>CUSTOMER PHONE NUMBER</label>
                                <Input
                                    placeholder="e.g. 9415332242"
                                    value={simPhone}
                                    onChange={e => setSimPhone(e.target.value)}
                                    style={{
                                        background: "#1e293b",
                                        border: "1px solid #475569",
                                        color: "#fff",
                                        borderRadius: "8px",
                                        height: "36px"
                                    }}
                                />
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8" }}>PRODUCT ID / MSN</label>
                                <Input
                                    placeholder="e.g. MSNO5WN3316R51"
                                    value={simProductId}
                                    onChange={e => setSimProductId(e.target.value)}
                                    style={{
                                        background: "#1e293b",
                                        border: "1px solid #475569",
                                        color: "#fff",
                                        borderRadius: "8px",
                                        height: "36px"
                                    }}
                                />
                            </div>

                            {simStatus === "error" && (
                                <div style={{
                                    background: "rgba(239, 68, 68, 0.15)",
                                    border: "1px solid rgba(239, 68, 68, 0.3)",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    fontSize: "12px",
                                    color: "#fca5a5"
                                }}>
                                    ❌ {simStatusText}
                                </div>
                            )}

                            {simStatus === "ended" && (
                                <div style={{
                                    background: "rgba(16, 185, 129, 0.15)",
                                    border: "1px solid rgba(16, 185, 129, 0.3)",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    fontSize: "12px",
                                    color: "#a7f3d0",
                                    textAlign: "center"
                                }}>
                                    📞 Call simulation ended successfully.
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Active Call Screen */
                        <div style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            padding: "14px 14px 0 14px"
                        }}>
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                padding: "12px 0 16px 0",
                                position: "relative"
                            }}>
                                {/* Audio Wave Pulsing rings */}
                                <div style={{
                                    width: "80px",
                                    height: "80px",
                                    borderRadius: "50%",
                                    background: simStatus === "connected" ? (isBotSpeaking ? "rgba(234, 88, 12, 0.2)" : isUserSpeaking ? "rgba(59, 130, 246, 0.2)" : "rgba(30, 41, 59, 0.4)") : "rgba(30, 41, 59, 0.4)",
                                    border: "1px solid #475569",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                    marginBottom: "12px",
                                    transition: "all 0.3s ease"
                                }}>
                                    {simStatus === "connected" && (isBotSpeaking || isUserSpeaking) && (
                                        <div style={{
                                            position: "absolute",
                                            inset: "-8px",
                                            border: isBotSpeaking ? "2px solid #ea580c" : "2px solid #3b82f6",
                                            borderRadius: "50%",
                                            animation: "pulse-ring 1.5s infinite ease-in-out"
                                        }} />
                                    )}
                                    <div style={{
                                        width: "64px",
                                        height: "64px",
                                        borderRadius: "50%",
                                        background: isBotSpeaking ? "#ea580c" : isUserSpeaking ? "#3b82f6" : "#475569",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "24px",
                                        color: "#fff",
                                        transition: "all 0.3s"
                                    }}>
                                        🤖
                                    </div>
                                </div>

                                <div style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    letterSpacing: "1px",
                                    textTransform: "uppercase",
                                    padding: "3px 10px",
                                    borderRadius: "12px",
                                    background: isBotSpeaking ? "#ea580c" : isUserSpeaking ? "#3b82f6" : "#334155",
                                    color: "#fff",
                                    marginBottom: "4px"
                                }}>
                                    {isBotSpeaking ? "Samaira Speaking..." : isUserSpeaking ? "You Speaking..." : simStatus === "connected" ? "Listening..." : "Connecting..."}
                                </div>

                                <div style={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>
                                    {simPhone}
                                </div>
                                <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                                    {simStatus === "connected" ? formatTimer(simTimer) : simStatusText}
                                </div>
                            </div>

                            {/* Sleek Voice Wave Visualization */}
                            <div style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(15, 23, 42, 0.4)",
                                border: "1px solid #1e293b",
                                borderRadius: "16px",
                                padding: "20px",
                                textAlign: "center"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", height: "48px" }}>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((bar) => {
                                        const delay = bar * 0.1;
                                        return (
                                            <span 
                                                key={bar} 
                                                style={{
                                                    width: "3px",
                                                    height: simStatus === "connected" ? "36px" : "6px",
                                                    background: isBotSpeaking ? "linear-gradient(to top, #f97316, #ea580c)" : isUserSpeaking ? "linear-gradient(to top, #3b82f6, #2563eb)" : "#475569",
                                                    borderRadius: "2px",
                                                    animation: simStatus === "connected" && (isBotSpeaking || isUserSpeaking) 
                                                        ? "voiceWave 0.8s ease-in-out infinite alternate" 
                                                        : "none",
                                                    animationDelay: `${delay}s`,
                                                    transition: "all 0.3s ease"
                                                }} 
                                            />
                                        );
                                    })}
                                </div>
                                <p style={{ fontSize: "11px", color: "#64748b", margin: "14px 0 0 0", fontWeight: 500 }}>
                                    {simStatus === "connected" ? "Bidirectional WebRTC session active" : "Negotiating Ozonetel secure channel..."}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Bottom controls */}
                    <div style={{
                        height: "90px",
                        display: "flex",
                        justifyContent: "space-around",
                        alignItems: "center",
                        padding: "0 24px",
                        background: "rgba(15, 23, 42, 0.8)",
                        borderTop: "1px solid #1e293b"
                    }}>
                        <Button
                            type="text"
                            shape="circle"
                            disabled={simStatus !== "connected"}
                            icon={isMuted ? <AudioMutedOutlined style={{ fontSize: "20px", color: "#ef4444" }} /> : <AudioOutlined style={{ fontSize: "20px", color: "#cbd5e1" }} />}
                            onClick={toggleMute}
                            style={{
                                width: "50px",
                                height: "50px",
                                background: isMuted ? "rgba(239, 68, 68, 0.1)" : "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.1)"
                            }}
                        />

                        {simStatus === "idle" || simStatus === "error" || simStatus === "ended" ? (
                            <Button
                                type="primary"
                                shape="circle"
                                icon={<PhoneOutlined style={{ fontSize: "24px" }} />}
                                onClick={initiateCall}
                                className="bg-green-600 hover:bg-green-500 border-none"
                                style={{
                                    width: "60px",
                                    height: "60px",
                                    background: "#16a34a",
                                    boxShadow: "0 4px 12px rgba(22, 163, 74, 0.4)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            />
                        ) : (
                            <Button
                                type="primary"
                                shape="circle"
                                danger
                                icon={<PhoneOutlined rotate={135} style={{ fontSize: "24px" }} />}
                                onClick={disconnectCall}
                                className="bg-red-600 hover:bg-red-500 border-none"
                                style={{
                                    width: "60px",
                                    height: "60px",
                                    background: "#dc2626",
                                    boxShadow: "0 4px 12px rgba(220, 38, 38, 0.4)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            />
                        )}

                        <Button
                            type="text"
                            shape="circle"
                            icon={<InfoCircleOutlined style={{ fontSize: "20px", color: "#cbd5e1" }} />}
                            onClick={() => {
                                Modal.info({
                                    title: "Pipecat WebRTC Simulator Info",
                                    content: (
                                        <div style={{ fontSize: "12px", display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
                                            <p><b>WebSocket Endpoint:</b> Outbound audio/transcripts routed via <code>ProtobufFrameSerializer</code>.</p>
                                            <p><b>Serialization:</b> Protobuf wrappers encapsulating audio, text, transcript, and JSON message types.</p>
                                            <p><b>Voice Processing:</b> Fully bidirectional audio streaming with sub-100ms latency.</p>
                                        </div>
                                    )
                                });
                            }}
                            style={{
                                width: "50px",
                                height: "50px",
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.1)"
                            }}
                        />
                    </div>
                </div>
            </Modal>

            {/* Global CSS for call animation waves */}
            <style>{`
                @keyframes pulse-ring {
                    0% { transform: scale(0.95); opacity: 1; }
                    50% { transform: scale(1.15); opacity: 0.5; }
                    100% { transform: scale(1.35); opacity: 0; }
                }
                @keyframes voiceWave {
                    0% { transform: scaleY(0.3); opacity: 0.6; }
                    100% { transform: scaleY(1.4); opacity: 1; }
                }
            `}</style>
        </StitchLayout>
    );
};

export default SamairaCallTrack;
